import { requireSupabase } from "./supabase";
import type { ConnectionStatus, RouteGeometry } from "../../../shared/strava";
export type StravaStatus = ConnectionStatus & {
  features: { enabled: boolean; links: boolean; routes: boolean };
};

export async function stravaRequest<T>(
  action: string,
  body?: Record<string, unknown>,
  query?: Record<string, string>,
): Promise<T> {
  const { data } = await requireSupabase().auth.getSession();
  if (!data.session) throw new Error("Logg inn på nytt.");
  const search = new URLSearchParams({ action, ...query });
  const response = await fetch(`/.netlify/functions/strava?${search}`, {
    method: body ? "POST" : "GET",
    cache: "no-store",
    credentials: "same-origin",
    headers: {
      authorization: `Bearer ${data.session.access_token}`,
      ...(body ? { "content-type": "application/json" } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!response.headers.get("content-type")?.includes("application/json"))
    throw new Error("Strava er ikke tilgjengelig i dette miljøet.");
  const result = await response.json();
  if (!response.ok)
    throw new Error(result.error || "Strava kunne ikke hentes.");
  return result as T;
}
export function normalizeRoute(value: unknown): RouteGeometry | null {
  if (!value || typeof value !== "object") return null;
  const r = value as RouteGeometry;
  if (
    typeof r.name !== "string" ||
    !Number.isFinite(r.distance_m) ||
    r.distance_m < 0 ||
    !Array.isArray(r.coordinates) ||
    r.coordinates.length < 2 ||
    r.coordinates.length > 20_000
  )
    return null;
  if (
    !r.coordinates.every(
      (p) =>
        Array.isArray(p) &&
        p.length === 2 &&
        Number.isFinite(p[0]) &&
        Number.isFinite(p[1]) &&
        Math.abs(p[0]) <= 90 &&
        Math.abs(p[1]) <= 180,
    )
  )
    return null;
  return { name: r.name, distance_m: r.distance_m, coordinates: r.coordinates };
}
export function safeStravaLink(url: unknown): string | null {
  return typeof url === "string" &&
    /^https:\/\/www\.strava\.com\/activities\/[1-9]\d{0,24}$/.test(url)
    ? url
    : null;
}
const draftPrefix = "join-strava-draft-v1:";
export function savePlanDraft(user: string, draft: unknown) {
  sessionStorage.setItem(
    draftPrefix + user,
    JSON.stringify({ expires: Date.now() + 3_600_000, draft }),
  );
}
export function consumePlanDraft<T>(user: string): T | null {
  try {
    const raw = sessionStorage.getItem(draftPrefix + user);
    if (!raw) return null;
    const value = JSON.parse(raw);
    return value.expires > Date.now() &&
      value.draft &&
      typeof value.draft === "object"
      ? (value.draft as T)
      : null;
  } catch {
    return null;
  }
}
export function clearStravaDrafts() {
  try {
    Object.keys(sessionStorage)
      .filter((key) => key.startsWith(draftPrefix))
      .forEach((key) => sessionStorage.removeItem(key));
  } catch {
    /* Storage may be disabled. */
  }
}
